import React, { useState } from 'react';
import {
    Menu,
    Today,
    CalendarToday,
    DateRange,
    Logout,
    Person,
    Notifications,
    ExpandMore,
    Business,
    SwapHoriz
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { storageKeys, hosts } from '../../config/environment';
import {
    getOrganizationRoleKeys,
    getRoleLabel,
    organizationNeedsRoleChoice,
} from '../../utils/organizationRoleUtils';
import { toEmailString } from '../../utils/toEmailString';

const TimeRangeButton = ({ active, onClick, children, icon: Icon }) => (
    <button
        onClick={onClick}
        className={`
            flex items-center space-x-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 relative overflow-hidden group
            ${active
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25'
            : 'text-gray-600 hover:text-amber-700 hover:bg-amber-50/50 border border-transparent hover:border-amber-200/50'
        }
        `}
    >
        {/* Effet de brillance sur le bouton actif */}
        {active && (
            <div className="absolute inset-0 bg-white/10 rounded-xl animate-pulse"></div>
        )}

        <Icon className={`text-lg ${active ? 'text-white' : 'text-amber-600'}`} />
        <span className="relative z-10">{children}</span>

        {/* Effet de surbrillance au survol */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
    </button>
);

const CurrencyButton = ({ active, onClick, children, isBusy }) => (
    <button
        onClick={onClick}
        className={`
            flex items-center space-x-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 relative overflow-hidden group
            ${active
            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25'
            : 'text-gray-600 hover:text-amber-700 hover:bg-amber-50/50 border border-transparent hover:border-amber-200/50'
        }
        `}
    >
        {/* Effet de brillance sur le bouton actif */}
        {active && (
            <div className="absolute inset-0 bg-white/10 rounded-xl animate-pulse"></div>
        )}

        <span className="relative z-10 flex items-center space-x-2">
            <span>{children}</span>
            {isBusy && active && (
                <span className="inline-flex items-center">
                    <span className="w-3 h-3 border-2 border-white/80 border-t-transparent rounded-full animate-spin"></span>
                </span>
            )}
        </span>

        {/* Effet de surbrillance au survol */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
    </button>
);

const DashboardHeader = ({
                             timeRange,
                             onTimeRangeChange,
                             currency,
                             onCurrencyChange,
                             currencyLoading = false,
                             onMenuToggle
                         }) => {
    const navigate = useNavigate();
    const { user, logout, selectedOrganization, organizations, selectOrganization } = useAuth();
    const { unViewed } = useNotification();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showOrgMenu, setShowOrgMenu] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const avatarUrl = (user?.id && hosts.api)
        ? `${hosts.api}/exposed/v1/mda/images/show?target=avatar&target_id=${user.id}`
        : null;
    const displayUser = {
        name: user?.name || user?.phoneNumber || toEmailString(user?.email) || 'Utilisateur',
        email: toEmailString(user?.email) || user?.phoneNumber || '',
        role: getRoleLabel(
            selectedOrganization?.selectedRole ||
                (selectedOrganization?.role === 'both' ? 'admin' : selectedOrganization?.role)
        ),
        avatar: avatarUrl || user?.avatar || null
    };

    const handleSwitchOrganization = (orgId, role) => {
        try {
            selectOrganization(orgId, role);
            setShowOrgMenu(false);
            // Recharger la page pour mettre à jour les données
            window.location.reload();
        } catch (error) {
            console.error('Erreur lors du changement d\'organisation:', error);
            alert(error.message || 'Erreur lors du changement d\'organisation');
        }
    };

    // Récupérer toutes les organisations disponibles
    const availableOrganizations = organizations && organizations.length > 0 
        ? organizations 
        : JSON.parse(localStorage.getItem(storageKeys.organizations) || '[]');

    const userMenuItems = [
        {
            label: 'Mon Profil',
            icon: Person,
            // Profil géré sur le compte Netikash (devaccounts en dev, accounts en prod)
            action: () => window.open(`${hosts.accounts}/`, '_blank', 'noopener,noreferrer'),
            color: 'text-gray-700 hover:bg-gray-50'
        },
        {
            label: 'Déconnexion',
            icon: Logout,
            action: handleLogout,
            color: 'text-red-600 hover:bg-red-50'
        }
    ];

    return (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
            {/* Left Section - Title and Menu */}
            <div className="flex items-center space-x-4">
                <button
                    onClick={onMenuToggle}
                    className="lg:hidden p-3 text-gray-600 hover:text-amber-600 hover:bg-amber-50/50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md backdrop-blur-sm"
                >
                    <Menu className="text-2xl" />
                </button>

                <div className="flex items-center space-x-4">
                    {/* Logo Flow — unique logo visible */}
                    <div className="hidden sm:flex">
                        <div className="w-12 h-12 rounded-2xl bg-white/90 backdrop-blur-sm border border-amber-200/60 shadow-md shadow-amber-500/10 flex items-center justify-center overflow-hidden p-1.5 ring-1 ring-slate-900/5">
                            <img
                                src="/images/Logo_Netikash%20Flow%20-%20Logo%20Only%20512x512.png"
                                alt="Flow"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>

                    <div>
                        {/* Brand Title */}
                        <div className="flex items-center space-x-3">
                            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                Flow
                            </h1>
                            <span className="text-gray-400 font-light text-lg hidden sm:block">|</span>
                            <span className="text-lg sm:text-xl text-amber-600 font-semibold hidden sm:block">
                                By Netikash
                            </span>
                        </div>

                        {/* Welcome Message avec sélecteur d'organisation */}
                        <div className="text-gray-500 text-sm mt-1 hidden sm:block">
                            {selectedOrganization ? (
                                <div className="flex items-center space-x-2">
                                    <span>Organisation:</span>
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowOrgMenu(!showOrgMenu)}
                                            className="flex items-center space-x-1 font-semibold text-amber-600 hover:text-amber-700 transition-colors group"
                                        >
                                            <Business className="w-4 h-4" />
                                            <span>{selectedOrganization.name || selectedOrganization.companyName}</span>
                                            <SwapHoriz className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                        
                                        {/* Menu de sélection d'organisation */}
                                        {showOrgMenu && (
                                            <div className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-xl rounded-2xl border border-white/40 shadow-2xl py-2 z-50">
                                                {organizationNeedsRoleChoice(selectedOrganization) && (
                                                    <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                                                        <p className="font-semibold text-gray-900 text-sm">Changer de rôle</p>
                                                        {getOrganizationRoleKeys(selectedOrganization).map((roleKey) => {
                                                            const isActive =
                                                                (selectedOrganization.selectedRole || 'admin') === roleKey;
                                                            return (
                                                                <button
                                                                    key={roleKey}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleSwitchOrganization(
                                                                            selectedOrganization.id,
                                                                            roleKey
                                                                        )
                                                                    }
                                                                    disabled={isActive}
                                                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                                                                        isActive
                                                                            ? 'bg-amber-100 text-amber-800 font-medium'
                                                                            : 'text-gray-700 hover:bg-gray-50'
                                                                    }`}
                                                                >
                                                                    {getRoleLabel(roleKey)}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {availableOrganizations.length > 1 && (
                                                <>
                                                <div className="px-4 py-2 border-b border-gray-100">
                                                    <p className="font-semibold text-gray-900 text-sm">Changer d&apos;organisation</p>
                                                </div>
                                                <div className="max-h-64 overflow-y-auto">
                                                    {availableOrganizations.map((org) => {
                                                        const isCurrent = org.id === selectedOrganization.id;
                                                        return (
                                                            <button
                                                                key={org.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    if (organizationNeedsRoleChoice(org)) {
                                                                        setShowOrgMenu(false);
                                                                        navigate('/auth/organizations');
                                                                    } else {
                                                                        handleSwitchOrganization(org.id, org.role);
                                                                    }
                                                                }}
                                                                disabled={isCurrent}
                                                                className={`w-full flex items-center space-x-3 px-4 py-3 transition-all duration-200 text-left ${
                                                                    isCurrent
                                                                        ? 'bg-amber-50 text-amber-700 cursor-not-allowed'
                                                                        : 'text-gray-700 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                <Business className={`text-lg ${isCurrent ? 'text-amber-600' : 'text-gray-400'}`} />
                                                                <div className="flex-1">
                                                                    <p className="font-medium">{org.name}</p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {organizationNeedsRoleChoice(org)
                                                                            ? 'Admin & Caissier'
                                                                            : getRoleLabel(org.role)}
                                                                    </p>
                                                                </div>
                                                                {isCurrent && (
                                                                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    Bon retour, <span className="font-semibold text-amber-600">{displayUser.name}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section - Controls and User Menu */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                {/* Time Range Selector */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-white/40 shadow-lg">
                    <div className="flex items-center space-x-1">
                        <TimeRangeButton
                            active={timeRange === 'today'}
                            onClick={() => onTimeRangeChange('today')}
                            icon={Today}
                        >
                            <span className="hidden sm:inline">Aujourd'hui</span>
                            <span className="sm:hidden">Jour</span>
                        </TimeRangeButton>
                        <TimeRangeButton
                            active={timeRange === 'week'}
                            onClick={() => onTimeRangeChange('week')}
                            icon={CalendarToday}
                        >
                            <span className="hidden sm:inline">Semaine</span>
                            <span className="sm:hidden">Sem</span>
                        </TimeRangeButton>
                        <TimeRangeButton
                            active={timeRange === 'month'}
                            onClick={() => onTimeRangeChange('month')}
                            icon={DateRange}
                        >
                            <span className="hidden sm:inline">Mois</span>
                            <span className="sm:hidden">Mois</span>
                        </TimeRangeButton>
                    </div>
                </div>

                {/* Currency Selector */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-white/40 shadow-lg">
                    <div className="flex items-center space-x-1">
                        <CurrencyButton
                            active={currency === 'USD'}
                            onClick={() => onCurrencyChange('USD')}
                            isBusy={currencyLoading}
                        >
                            USD
                        </CurrencyButton>
                        <CurrencyButton
                            active={currency === 'CDF'}
                            onClick={() => onCurrencyChange('CDF')}
                            isBusy={currencyLoading}
                        >
                            CDF
                        </CurrencyButton>
                    </div>
                </div>

                {/* User Menu */}
                <div className="relative">
                    <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-2xl p-3 border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 group"
                    >
                        {/* Avatar via API (comme sur mobile) */}
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md overflow-hidden">
                            {displayUser.avatar ? (
                                <img
                                    src={displayUser.avatar}
                                    alt={displayUser.name}
                                    className="w-full h-full rounded-xl object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }}
                                />
                            ) : null}
                            <span className={`text-white font-semibold text-sm ${displayUser.avatar ? 'hidden' : ''}`}>
                                {displayUser.name.split(' ').filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'U'}
                            </span>
                        </div>

                        {/* User Info (visible sur desktop) */}
                        <div className="hidden sm:block text-left">
                            <p className="font-semibold text-gray-900 text-sm">{displayUser.name}</p>
                            <p className="text-gray-500 text-xs">{displayUser.role}</p>
                        </div>

                        {/* Chevron Icon */}
                        <ExpandMore className={`text-gray-400 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} />

                        {/* Effet de surbrillance */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-amber-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                        <div className="absolute right-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-xl rounded-2xl border border-white/40 shadow-2xl py-2 z-50">
                            {/* Header du menu */}
                            <div className="px-4 py-3 border-b border-gray-100">
                                <p className="font-semibold text-gray-900">{displayUser.name}</p>
                                <p className="text-gray-500 text-sm">{displayUser.email || displayUser.name}</p>
                            </div>

                            {/* Menu Items */}
                            <div className="py-2">
                                {userMenuItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                item.action();
                                                setShowUserMenu(false);
                                            }}
                                            className={`w-full flex items-center space-x-3 px-4 py-3 transition-all duration-200 ${item.color}`}
                                        >
                                            <Icon className="text-lg" />
                                            <span className="font-medium">{item.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Footer du menu */}
                            <div className="px-4 py-2 border-t border-gray-100">
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span>En ligne • Dernière connexion: Maintenant</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bouton de notification */}
                <button 
                    onClick={() => navigate('/admin/notifications')}
                    className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 relative group"
                >
                    <Notifications className="text-gray-600 group-hover:text-amber-600 text-xl" />
                    {unViewed > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{unViewed > 99 ? '99+' : unViewed}</span>
                        </div>
                    )}

                    {/* Effet de surbrillance */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-blue-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </button>
            </div>

            {/* Overlay pour fermer les menus */}
            {(showUserMenu || showOrgMenu) && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => {
                        setShowUserMenu(false);
                        setShowOrgMenu(false);
                    }}
                />
            )}
        </div>
    );
};

export default DashboardHeader;